import { yieldToMainThread } from "@theme/utilities";
import { Component } from "@theme/component";

/** Max time to keep a fly-to-cart node in DOM if animations never finish. */
const FLY_TO_CART_REMOVE_MS = 3000;

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #removeFallbackTimer;

  connectedCallback() {
    super.connectedCallback();

    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    this.#removeFallbackTimer = setTimeout(() => {
      this.remove();
    }, FLY_TO_CART_REMOVE_MS);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const sourceRect = this.source.getBoundingClientRect();
        const destinationRect = this.destination.getBoundingClientRect();

        if (sourceRect.width <= 0 || sourceRect.height <= 0) {
          this.#clearFallbackAndRemove();
          return;
        }

        this.#animate(sourceRect, destinationRect).finally(() => {
          this.#clearFallbackAndRemove();
        });
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#removeFallbackTimer) clearTimeout(this.#removeFallbackTimer);
  }

  #clearFallbackAndRemove() {
    if (this.#removeFallbackTimer) {
      clearTimeout(this.#removeFallbackTimer);
      this.#removeFallbackTimer = undefined;
    }
    this.remove();
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty("--width", `${sourceRect.width}px`);
      this.style.setProperty("--height", `${sourceRect.height}px`);
    }
    this.style.setProperty("--start-x", `${startPoint.x}px`);
    this.style.setProperty("--start-y", `${startPoint.y}px`);
    this.style.setProperty("--travel-x", `${endPoint.x - startPoint.x}px`);
    this.style.setProperty("--travel-y", `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    const animations = this.getAnimations();
    if (!animations.length) return;

    await Promise.allSettled(animations.map((a) => a.finished));
  };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
